import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { UserRole } from '@/api/admin-types'
import { useAcceptBillingPlan } from '@/api/api-mutations'
import { preloadGetBillingPlan, useGetBillingPlan, useGetMe } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { EmptyState } from '@/components/base/empty-state'
import { Heading } from '@/components/base/heading'
import { LoadingSpinner } from '@/components/base/loading-spinner'
import { usePrompt } from '@/components/base/prompt'
import { SectionRow } from '@/components/base/section-row'
import { StatusBadge } from '@/components/base/status-badge'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { useI18n } from '@/i18n/i18n-react'
import { fromPrecision } from '@/utils/currency'

export const Route = createFileRoute('/_authenticated/settings/billing/')({
	component: BillingSettings,
	loader: async ({ context }) => await preloadGetBillingPlan(context, {}),
	staticData: { crumb: ({ context: { L } }) => L.Settings.Billing.Title() }
})

function BillingSettings() {
	const { L, locale } = useI18n()
	const B = L.Settings.Billing
	const me = useGetMe()
	const billing = useGetBillingPlan()
	const prompt = usePrompt()
	const acceptPlan = useAcceptBillingPlan()
	const [isAccepting, setIsAccepting] = useState(false)

	const isAdmin = me.data?.role === UserRole.Admin
	const data = billing.data
	const plan = data?.plan ?? null

	const formatFee = (amount: { value: number; precision: number }, currencyCode: string): string => {
		const value = fromPrecision(amount)
		try {
			return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode }).format(value)
		} catch {
			// Fall back to a plain amount + code if the currency isn't recognised by Intl.
			return `${value} ${currencyCode}`
		}
	}

	const handleAccept = async () => {
		if (!plan) {
			return
		}
		const confirmed = await prompt({
			title: B.AcceptConfirmTitle(),
			description: B.AcceptConfirmDescription(),
			confirmText: B.AcceptConfirmAction(),
			cancelText: L.General.Cancel()
		})
		if (!confirmed) {
			return
		}
		setIsAccepting(true)
		const result = await acceptPlan({})
		setIsAccepting(false)
		if (result.isSuccess) {
			toast.success(B.AcceptSuccess())
		} else {
			toast.error(B.AcceptError())
		}
	}

	return (
		<SingleColumnPage>
			<div className='space-y-6'>
				<div>
					<Heading level='h1'>{B.Title()}</Heading>
					<p className='mt-2 text-sm text-ui-fg-subtle'>{B.Description()}</p>
				</div>

				{billing.isPending ? (
					<LoadingSpinner />
				) : billing.isError ? (
					<p className='txt-compact-small text-ui-fg-subtle'>{B.LoadError()}</p>
				) : !plan ? (
					<EmptyState
						title={B.EmptyTitle()}
						description={
							<>
								{B.EmptyDescription()} {B.EmptyContactPrefix()}{' '}
								<a
									href='mailto:support@fast-track.io'
									className='font-medium text-ui-fg-interactive outline-none transition-fg hover:text-ui-fg-interactive-hover'
								>
									{B.EmptyContactLink()}
								</a>{' '}
								{B.EmptyContactSuffix()}
							</>
						}
						className='min-h-[240px]'
					/>
				) : (
					<div className='space-y-6'>
						<Container className='divide-y p-0'>
							<ContainerHeader
								heading={B.PlanHeading()}
								rightSide={
									!plan.acceptedAt && isAdmin ? (
										<Button variant='primary' onClick={handleAccept} isLoading={isAccepting}>
											{B.Accept()}
										</Button>
									) : undefined
								}
							/>
							<SectionRow title={B.Amount()} value={formatFee(plan.amount, plan.currencyCode)} />
							<SectionRow
								title={B.Interval()}
								value={plan.interval === 'YEARLY' ? B.Yearly() : B.Monthly()}
							/>
							<SectionRow title={B.StartDate()} value={L.Date.Short(plan.startDate)} />
							<SectionRow
								title={B.Status()}
								value={
									plan.acceptedAt ? (
										<StatusBadge color='green'>{B.StatusAccepted()}</StatusBadge>
									) : (
										<StatusBadge color='orange'>{B.StatusPending()}</StatusBadge>
									)
								}
							/>
							{plan.acceptedAt && (
								<SectionRow
									title={B.AcceptedBy()}
									value={[plan.acceptedBy, L.Date.DateAndTime(plan.acceptedAt)]
										.filter(Boolean)
										.join(' · ')}
								/>
							)}
						</Container>

						{plan.parameters.length > 0 && (
							<Container className='divide-y p-0'>
								<ContainerHeader heading={B.PlanDetails()} />
								{plan.parameters.map((parameter, index) => (
									<SectionRow
										key={`${parameter.label}-${index}`}
										title={parameter.label}
										value={parameter.value}
									/>
								))}
							</Container>
						)}

						{plan.lineItems.length > 0 && (
							<Container className='divide-y p-0'>
								<ContainerHeader heading={B.AdditionalFees()} />
								{plan.lineItems.map((lineItem, index) => (
									<SectionRow
										key={`${lineItem.label}-${index}`}
										title={lineItem.label}
										value={[
											formatFee(lineItem.amount, plan.currencyCode),
											lineItem.interval === 'YEARLY' ? B.Yearly() : B.Monthly()
										].join(' · ')}
									/>
								))}
							</Container>
						)}
					</div>
				)}
			</div>
		</SingleColumnPage>
	)
}
